"use client";

import { useState, useCallback } from "react";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Filesystem } from "@capacitor/filesystem";

// Custom Vision OCR plugin interface
interface VisionOCRPlugin {
  detectText(options: { base64Image: string }): Promise<{
    text: string;
    blocks: Array<{ text: string; lines: Array<{ text: string }> }>;
  }>;
}

// Register our custom Vision OCR plugin
const VisionOCR = registerPlugin<VisionOCRPlugin>("VisionOCR");

// Helper to convert Blob to base64
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove the data URL prefix
      const base64Data = result.split(",")[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export interface ScanResult {
  text: string;
  blocks: Array<{
    text: string;
    lines: Array<{
      text: string;
    }>;
  }>;
}

interface ScanResponse {
  result: ScanResult | null;
  error: string | null;
}

interface MultiScanResponse {
  results: ScanResult[];
  error: string | null;
}

interface UseScannerReturn {
  scan: () => Promise<ScanResponse>;
  scanMultiple: () => Promise<MultiScanResponse>;
  isScanning: boolean;
  error: string | null;
  isNative: boolean;
}

export function useBlockScanner(): UseScannerReturn {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isNative = Capacitor.isNativePlatform();

  const scan = useCallback(async (): Promise<ScanResponse> => {
    if (!isNative) {
      const errMsg = "Block scanning is only available in the mobile app";
      setError(errMsg);
      return { result: null, error: errMsg };
    }

    setIsScanning(true);
    setError(null);

    try {
      // Request permission and pick image from gallery
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Photos,
      });

      if (!photo.base64String) {
        throw new Error("No image selected");
      }

      // Run OCR on the image using Apple Vision framework
      console.log("[BlockScanner] Running OCR on image...");
      const result = await VisionOCR.detectText({
        base64Image: photo.base64String,
      });

      console.log("[BlockScanner] OCR result:", JSON.stringify(result, null, 2));

      if (!result.text || result.text.trim().length === 0) {
        throw new Error("No text found in image");
      }

      return {
        result: {
          text: result.text,
          blocks: result.blocks.map((block) => ({
            text: block.text,
            lines: block.lines.map((line) => ({
              text: line.text,
            })),
          })),
        },
        error: null,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to scan image";
      // Handle user cancellation gracefully
      if (message.includes("cancelled") || message.includes("canceled") || message.includes("User cancelled")) {
        setError(null);
        return { result: null, error: null };
      }
      setError(message);
      return { result: null, error: message };
    } finally {
      setIsScanning(false);
    }
  }, [isNative]);

  const scanMultiple = useCallback(async (): Promise<MultiScanResponse> => {
    if (!isNative) {
      const errMsg = "Block scanning is only available in the mobile app";
      setError(errMsg);
      return { results: [], error: errMsg };
    }

    setIsScanning(true);
    setError(null);

    try {
      // Request permission and pick multiple images from gallery
      const photos = await Camera.pickImages({
        quality: 90,
        limit: 10, // Allow up to 10 images
      });

      if (!photos.photos || photos.photos.length === 0) {
        throw new Error("No images selected");
      }

      const results: ScanResult[] = [];

      // Process each image
      for (const photo of photos.photos) {
        try {
          // Read the image as base64 using Filesystem
          // On iOS, photo.path contains the native file path
          const filePath = photo.path || photo.webPath;
          if (!filePath) {
            console.error("[BlockScanner] No file path for photo");
            continue;
          }

          console.log("[BlockScanner] Reading file:", filePath);
          const fileData = await Filesystem.readFile({
            path: filePath,
          });

          // fileData.data is the base64 string
          const base64 = typeof fileData.data === "string"
            ? fileData.data
            : await blobToBase64(fileData.data);

          // Run OCR on the image using Apple Vision framework
          console.log("[BlockScanner] Running OCR on image...");
          const result = await VisionOCR.detectText({
            base64Image: base64,
          });

          console.log("[BlockScanner] OCR result:", result.text?.substring(0, 100));

          if (result.text && result.text.trim().length > 0) {
            results.push({
              text: result.text,
              blocks: result.blocks.map((block) => ({
                text: block.text,
                lines: block.lines.map((line) => ({
                  text: line.text,
                })),
              })),
            });
          }
        } catch (imgErr) {
          console.error("[BlockScanner] Error processing image:", imgErr);
          // Continue with other images
        }
      }

      if (results.length === 0) {
        throw new Error("No text found in any of the selected images");
      }

      return { results, error: null };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to scan images";
      // Handle user cancellation gracefully
      if (message.includes("cancelled") || message.includes("canceled") || message.includes("User cancelled")) {
        setError(null);
        return { results: [], error: null };
      }
      setError(message);
      return { results: [], error: message };
    } finally {
      setIsScanning(false);
    }
  }, [isNative]);

  return {
    scan,
    scanMultiple,
    isScanning,
    error,
    isNative,
  };
}
